import React, { useEffect, useState } from 'react';
import {
    Card,
    Row,
    Col,
    Select,
    Button,
    Spin,
    message,
    Modal,
    Tag,
    Tooltip,
    Empty,
    Space,
    Divider,
    Statistic,
    Input,
    Table,
    Popconfirm,
    Badge,
    Alert,
} from 'antd';
import {
    CalendarOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    CloseCircleOutlined,
    PlusOutlined,
    ReloadOutlined,
    InfoCircleOutlined,
    SearchOutlined,
    DashboardOutlined,
    DeleteOutlined,
    BellOutlined,
    HistoryOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

const SupplierControl = () => {
    const { t } = useTranslation();

    const [suppliers, setSuppliers] = useState([]);
    const [filteredSuppliers, setFilteredSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(moment().year());
    const [selectedSuppliers, setSelectedSuppliers] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [upcomingAudits, setUpcomingAudits] = useState([]);
    const [auditHistory, setAuditHistory] = useState([]);
    const [selectedSupplierForHistory, setSelectedSupplierForHistory] = useState(null);

    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("urlToken");

    // ✅ Fetch supplier data
    const fetchSuppliers = async () => {
        setLoading(true);
        try {
            console.log('🔄 Fetching suppliers...');

            const supplierResponse = await axios.get(`${apiUrl}/api/Suppliers`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const supplierData = supplierResponse.data.$values || supplierResponse.data || [];
            console.log('✅ Suppliers fetched:', supplierData.length);

            const filteredSuppliers = supplierData.filter(supplier => 
                supplier.role?.toLowerCase() === 'supplier'
            );

            const suppliersToUse = filteredSuppliers.length > 0 ? filteredSuppliers : supplierData;

            const suppliersWithData = await Promise.all(
                suppliersToUse.map(async (supplier) => {
                    try {
                        let audits = [];
                        try {
                            const auditResponse = await axios.get(
                                `${apiUrl}/api/SupplierAudit/supplier/${supplier.supplierId}`,
                                { headers: { Authorization: `Bearer ${token}` } }
                            );
                            audits = auditResponse.data.audits || [];
                        } catch (error) {
                            console.warn(`No audits for ${supplier.supplierName}`);
                        }

                        let auditFrequency = 1;
                        let supplierClass = 'N/A';
                        let latestPeriod = 'N/A';

                        try {
                            const performanceResponse = await axios.get(
                                `${apiUrl}/api/SupplierPerformance/supplier/${supplier.supplierId}`,
                                { headers: { Authorization: `Bearer ${token}` } }
                            );

                            const performanceData = performanceResponse.data.$values || performanceResponse.data || [];

                            if (performanceData.length > 0) {
                                const latestPerformance = performanceData.sort((a, b) =>
                                    moment(b.period, 'YYYY-MM').diff(moment(a.period, 'YYYY-MM'))
                                )[0];

                                auditFrequency = latestPerformance.auditFrequency || 1;
                                supplierClass = latestPerformance.supplierClass || 'N/A';
                                latestPeriod = latestPerformance.period || 'N/A';
                            }
                        } catch (error) {
                            console.warn(`No performance data for ${supplier.supplierName}`);
                        }

                        const filteredAudits = audits
                            .filter(audit => moment(audit.auditDate).year() === selectedYear)
                            .sort((a, b) => moment(a.auditDate) - moment(b.auditDate));

                        const allAudits = audits.sort((a, b) => moment(b.auditDate) - moment(a.auditDate));

                        return {
                            ...supplier,
                            audits: filteredAudits,
                            allAudits: allAudits,
                            auditFrequency,
                            supplierClass,
                            latestPeriod,
                        };
                    } catch (error) {
                        console.error(`Error fetching data for ${supplier.supplierName}:`, error);
                        return {
                            ...supplier,
                            audits: [],
                            allAudits: [],
                            auditFrequency: 1,
                            supplierClass: 'N/A',
                            latestPeriod: 'N/A',
                        };
                    }
                })
            );

            setSuppliers(suppliersWithData);
            setFilteredSuppliers(suppliersWithData);
            calculateUpcomingAudits(suppliersWithData);
        } catch (error) {
            console.error('Error fetching suppliers:', error);
            message.error(t('error_loading_data'));
        } finally {
            setLoading(false);
        }
    };

    const calculateUpcomingAudits = (suppliers) => {
        const now = moment();
        const upcoming = [];

        suppliers.forEach(supplier => {
            supplier.audits.forEach(audit => {
                const auditDate = moment(audit.auditDate);
                const diffDays = auditDate.diff(now, 'days');

                if (diffDays >= 0 && diffDays <= 30) {
                    upcoming.push({
                        ...audit,
                        supplierName: supplier.supplierName,
                        supplierCode: supplier.supplierCode,
                        daysUntil: diffDays,
                    });
                }
            });
        });

        upcoming.sort((a, b) => moment(a.auditDate) - moment(b.auditDate));
        setUpcomingAudits(upcoming);
    };

    useEffect(() => {
        fetchSuppliers();
    }, [selectedYear]);

    useEffect(() => {
        if (searchText.trim() === '') {
            setFilteredSuppliers(suppliers);
        } else {
            const filtered = suppliers.filter(supplier =>
                supplier.supplierName?.toLowerCase().includes(searchText.toLowerCase()) ||
                supplier.supplierCode?.toLowerCase().includes(searchText.toLowerCase())
            );
            setFilteredSuppliers(filtered);
        }
    }, [searchText, suppliers]);

    const handleSelectSupplier = (supplierId) => {
        if (selectedSuppliers.includes(supplierId)) {
            setSelectedSuppliers(selectedSuppliers.filter(id => id !== supplierId));
        } else {
            setSelectedSuppliers([...selectedSuppliers, supplierId]);
        }
    };

    const handleSelectAll = () => {
        if (selectedSuppliers.length === filteredSuppliers.length) {
            setSelectedSuppliers([]);
        } else {
            setSelectedSuppliers(filteredSuppliers.map(s => s.supplierId));
        }
    };

    const showModal = () => {
        if (selectedSuppliers.length === 0) {
            message.warning(t('please_select_suppliers'));
            return;
        }
        setIsModalVisible(true);
    };

    const handlePostSelectedSuppliers = async () => {
        if (!selectedMonth) {
            message.error(t('please_select_month'));
            return;
        }

        setLoading(true);

        try {
            await Promise.all(
                selectedSuppliers.map(async (supplierId) => {
                    const supplier = suppliers.find(s => s.supplierId === supplierId);
                    if (!supplier) return;

                    const selectedDate = moment(`${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-01`);
                    const auditFrequency = supplier.auditFrequency || 1;
                    const intervalMonths = 12 / auditFrequency;
                    let currentAuditDate = selectedDate;

                    for (let i = 0; i < auditFrequency * 2; i++) {
                        try {
                            await axios.post(
                                `${apiUrl}/api/SupplierAudit`,
                                {
                                    supplierId: supplier.supplierId,
                                    auditDate: currentAuditDate.format('YYYY-MM-DD'),
                                },
                                { headers: { Authorization: `Bearer ${token}` } }
                            );
                            currentAuditDate = moment(currentAuditDate).add(intervalMonths, 'months');
                        } catch (error) {
                            console.error(`Failed to create audit for ${supplierId}`);
                        }
                    }
                })
            );

            message.success(t('audit_dates_added_successfully'));
            setIsModalVisible(false);
            setSelectedSuppliers([]);
            setSelectedMonth(null);
            fetchSuppliers();
        } catch (error) {
            console.error('Error adding audit dates:', error);
            message.error(t('error_adding_audit_dates'));
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAudit = async (auditId, supplierName) => {
        try {
            await axios.delete(
                `${apiUrl}/api/SupplierAudit/${auditId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            message.success(`${supplierName} - ${t('audit_date_deleted')}`);
            fetchSuppliers();
        } catch (error) {
            console.error('Error deleting audit:', error);
            message.error(t('error_deleting_audit'));
        }
    };

    const showAuditHistory = (supplier) => {
        setSelectedSupplierForHistory(supplier);
        setAuditHistory(supplier.allAudits || []);
        setIsHistoryModalVisible(true);
    };

    const getAuditBadgeStatus = (auditDate) => {
        const now = moment();
        const auditMoment = moment(auditDate);
        const diffDays = auditMoment.diff(now, 'days');

        if (auditMoment.isBefore(now, 'day')) {
            return { status: 'error', color: 'red', text: t('past'), icon: <CloseCircleOutlined /> };
        } else if (diffDays < 30) {
            return { status: 'warning', color: 'orange', text: t('upcoming'), icon: <WarningOutlined /> };
        } else {
            return { status: 'success', color: 'green', text: t('scheduled'), icon: <CheckCircleOutlined /> };
        }
    };

    const getClassColor = (supplierClass) => {
        if (supplierClass === 'A') return 'green';
        if (supplierClass === 'B') return 'orange';
        if (supplierClass === 'C') return 'red';
        return 'default';
    };

    const stats = {
        total: suppliers.length,
        selected: selectedSuppliers.length,
        withAudits: suppliers.filter(s => s.audits.length > 0).length,
        withoutAudits: suppliers.filter(s => s.audits.length === 0).length,
        upcoming: upcomingAudits.length,
    };

    const historyColumns = [
        {
            title: t('audit_date'),
            dataIndex: 'auditDate',
            key: 'auditDate',
            render: (date) => (
                <span style={{ fontSize: '14px' }}>
                    <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    {moment(date).format('DD MMMM YYYY')}
                </span>
            ),
            sorter: (a, b) => moment(a.auditDate) - moment(b.auditDate),
        },
        {
            title: t('status'),
            dataIndex: 'auditDate',
            key: 'status',
            align: 'center',
            render: (date) => {
                const status = getAuditBadgeStatus(date);
                return (
                    <Tag icon={status.icon} color={status.color} style={{ fontSize: '13px' }}>
                        {status.text}
                    </Tag>
                );
            },
        },
        {
            title: t('days_until'),
            dataIndex: 'auditDate',
            key: 'daysUntil',
            align: 'center',
            render: (date) => {
                const diffDays = moment(date).diff(moment(), 'days');
                if (diffDays < 0) {
                    return <Tag color="red">{Math.abs(diffDays)} {t('days_ago')}</Tag>;
                } else {
                    return <Tag color="blue">{diffDays} {t('days')}</Tag>;
                }
            },
        },
        {
            title: t('actions'),
            key: 'actions',
            align: 'center',
            render: (_, record) => (
                <Popconfirm
                    title={t('delete_audit_confirm')}
                    onConfirm={() => handleDeleteAudit(record.supplierAuditId, selectedSupplierForHistory?.supplierName)}
                    okText={t('yes')}
                    cancelText={t('no')}
                >
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                    />
                </Popconfirm>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header */}
            <div style={{ 
                background: '#fff',
                padding: '32px',
                borderRadius: '12px',
                marginBottom: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #1890ff'
            }}>
                <h1 style={{ 
                    fontSize: '32px', 
                    fontWeight: '700', 
                    color: '#262626', 
                    margin: 0
                }}>
                    <DashboardOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
                    {t('audit_date_management')}
                </h1>
                <p style={{ color: '#8c8c8c', margin: '8px 0 0 0', fontSize: '15px' }}>
                    {t('manage_and_view_supplier_audit_dates')}
                </p>
            </div>

            {/* Upcoming Audits Alert */}
            {upcomingAudits.length > 0 && (
                <Alert
                    message={
                        <Space>
                            <BellOutlined style={{ fontSize: '16px' }} />
                            <strong>{t('upcoming_audits_notification')}</strong>
                        </Space>
                    }
                    description={
                        <div style={{ marginTop: '8px' }}>
                            {upcomingAudits.slice(0, 3).map((audit, index) => (
                                <div key={index} style={{ marginBottom: '4px' }}>
                                    <Tag color="orange" style={{ marginRight: '8px' }}>
                                        {audit.daysUntil} {t('days')}
                                    </Tag>
                                    <strong>{audit.supplierName}</strong> - {moment(audit.auditDate).format('DD MMM YYYY')}
                                </div>
                            ))}
                            {upcomingAudits.length > 3 && (
                                <div style={{ marginTop: '8px', color: '#1890ff', cursor: 'pointer' }}>
                                    +{upcomingAudits.length - 3} {t('more')}
                                </div>
                            )}
                        </div>
                    }
                    type="warning"
                    showIcon
                    closable
                    style={{ marginBottom: '24px', borderRadius: '12px' }}
                />
            )}

            {/* ✅ Statistics Cards - EŞİT YÜKSEKLIK */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card 
                        style={{ 
                            borderRadius: '12px', 
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            height: '100%' // ✅ Eşit yükseklik
                        }}
                    >
                        <Statistic
                            title={t('total_suppliers')}
                            value={stats.total}
                            prefix={<InfoCircleOutlined style={{ color: '#1890ff' }} />}
                            valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#1890ff' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card 
                        style={{ 
                            borderRadius: '12px', 
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            height: '100%' // ✅ Eşit yükseklik
                        }}
                    >
                        <Statistic
                            title={t('selected')}
                            value={stats.selected}
                            prefix={<CheckCircleOutlined style={{ color: '#722ed1' }} />}
                            valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#722ed1' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card 
                        style={{ 
                            borderRadius: '12px', 
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            height: '100%' // ✅ Eşit yükseklik
                        }}
                    >
                        <Statistic
                            title={t('with_audits')}
                            value={stats.withAudits}
                            prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                            valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#52c41a' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card 
                        style={{ 
                            borderRadius: '12px', 
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            height: '100%' // ✅ Eşit yükseklik
                        }}
                    >
                        <Badge count={stats.upcoming} offset={[10, 0]}>
                            <Statistic
                                title={t('upcoming_audits')}
                                value={stats.upcoming}
                                prefix={<BellOutlined style={{ color: '#faad14' }} />}
                                valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#faad14' }}
                            />
                        </Badge>
                    </Card>
                </Col>
            </Row>

            {/* ✅ Filters and Actions - TAMAMEN DÜZELTİLDİ */}
            <Card style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <Row gutter={[16, 16]} align="middle">
                    {/* Search Input */}
                    <Col xs={24} sm={24} md={10} lg={10}>
                        <Input
                            placeholder={t('search_supplier')}
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            size="large"
                            allowClear
                            style={{ width: '100%' }}
                        />
                    </Col>

                    {/* Year Filter */}
                    <Col xs={24} sm={12} md={4} lg={3}>
                        <Select
                            value={selectedYear}
                            style={{ width: '100%' }}
                            onChange={setSelectedYear}
                            size="large"
                        >
                            {Array.from({ length: 6 }, (_, index) => moment().year() - 2 + index).map((year) => (
                                <Option key={year} value={year}>
                                    {year}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    {/* Action Buttons */}
                    <Col xs={24} sm={12} md={10} lg={11}>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'flex-end', 
                            alignItems: 'center',
                            gap: '8px', 
                            flexWrap: 'wrap',
                            height: '100%' // ✅ Tam yükseklik
                        }}>
                            <Button
                                icon={<CheckCircleOutlined />}
                                onClick={handleSelectAll}
                                size="large"
                            >
                                {selectedSuppliers.length === filteredSuppliers.length ? t('deselect_all') : t('select_all')}
                            </Button>

                            <Button
                                icon={<ReloadOutlined />}
                                onClick={fetchSuppliers}
                                loading={loading}
                                size="large"
                            >
                                {t('refresh')}
                            </Button>

                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={showModal}
                                disabled={selectedSuppliers.length === 0}
                                size="large"
                            >
                                {t('add_audit_dates')} ({selectedSuppliers.length})
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* ✅ Supplier Cards - TAMAMEN DÜZELTİLDİ */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                    <Spin size="large" tip={t('loading')} />
                </div>
            ) : filteredSuppliers.length === 0 ? (
                <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <Empty 
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t('no_suppliers_found')} 
                    />
                </Card>
            ) : (
                <Row gutter={[16, 16]}>
                    {filteredSuppliers.map((supplier) => {
                        const isSelected = selectedSuppliers.includes(supplier.supplierId);
                        const hasUpcoming = supplier.audits.some(audit => {
                            const diffDays = moment(audit.auditDate).diff(moment(), 'days');
                            return diffDays >= 0 && diffDays <= 30;
                        });

                        return (
                            <Col xs={24} sm={12} md={12} lg={8} xl={6} key={supplier.supplierId}>
                                <Badge.Ribbon 
                                    text={hasUpcoming ? t('upcoming') : null} 
                                    color="orange"
                                    style={{ display: hasUpcoming ? 'block' : 'none' }}
                                >
                                    <Card
                                        hoverable
                                        onClick={() => handleSelectSupplier(supplier.supplierId)}
                                        style={{
                                            borderRadius: '12px',
                                            border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
                                            background: isSelected ? '#e6f7ff' : '#fff',
                                            boxShadow: isSelected ? '0 4px 12px rgba(24, 144, 255, 0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
                                            transition: 'all 0.3s ease',
                                            minHeight: '320px', // ✅ Minimum yükseklik
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                        bodyStyle={{ 
                                            padding: '20px',
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        {/* Header - Sabit Alan */}
                                        <div style={{ marginBottom: '16px' }}>
                                            <div style={{ 
                                                fontSize: '16px', 
                                                fontWeight: '600', 
                                                marginBottom: '8px',
                                                color: '#262626',
                                                minHeight: '48px', // ✅ Sabit yükseklik (2 satır için)
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                lineHeight: '24px',
                                            }}>
                                                {supplier.supplierName}
                                            </div>
                                            <div style={{ 
                                                fontSize: '13px', 
                                                color: '#8c8c8c', 
                                                marginBottom: '12px',
                                                minHeight: '20px' // ✅ Sabit yükseklik
                                            }}>
                                                {supplier.supplierCode}
                                            </div>
                                            <Space wrap>
                                                <Tag color={getClassColor(supplier.supplierClass)}>
                                                    {t('class')}: {supplier.supplierClass}
                                                </Tag>
                                                <Tag color="blue">
                                                    {supplier.auditFrequency}x/{t('year')}
                                                </Tag>
                                            </Space>
                                        </div>

                                        <Divider style={{ margin: '12px 0' }} />

                                        {/* Audit Dates - Flex Grow */}
                                        <div style={{ flex: 1, minHeight: '100px' }}> {/* ✅ Minimum yükseklik */}
                                            <div style={{ 
                                                fontSize: '14px', 
                                                fontWeight: '600', 
                                                marginBottom: '8px',
                                                color: '#595959',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                <span>{t('audit_dates')} ({supplier.audits.length})</span>
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    icon={<HistoryOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        showAuditHistory(supplier);
                                                    }}
                                                    style={{ padding: 0 }}
                                                >
                                                    {t('history')}
                                                </Button>
                                            </div>
                                            {supplier.audits.length === 0 ? (
                                                <Tag color="default" style={{ fontSize: '13px' }}>
                                                    {t('no_audits')}
                                                </Tag>
                                            ) : (
                                                <Space wrap size={[4, 8]}>
                                                    {supplier.audits.map((audit) => {
                                                        const badgeInfo = getAuditBadgeStatus(audit.auditDate);
                                                        return (
                                                            <Tooltip
                                                                key={audit.supplierAuditId}
                                                                title={
                                                                    <div>
                                                                        <div>{badgeInfo.text}: {moment(audit.auditDate).format('DD.MM.YYYY')}</div>
                                                                        <div style={{ marginTop: '4px', fontSize: '12px' }}>
                                                                            {t('click_to_delete')}
                                                                        </div>
                                                                    </div>
                                                                }
                                                            >
                                                                <Popconfirm
                                                                    title={t('delete_audit_confirm')}
                                                                    onConfirm={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeleteAudit(audit.supplierAuditId, supplier.supplierName);
                                                                    }}
                                                                    okText={t('yes')}
                                                                    cancelText={t('no')}
                                                                >
                                                                    <Tag
                                                                        icon={badgeInfo.icon}
                                                                        color={badgeInfo.color}
                                                                        style={{ 
                                                                            cursor: 'pointer',
                                                                            fontSize: '13px',
                                                                            padding: '4px 8px'
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        {moment(audit.auditDate).format('MMM DD')}
                                                                    </Tag>
                                                                </Popconfirm>
                                                            </Tooltip>
                                                        );
                                                    })}
                                                </Space>
                                            )}
                                        </div>

                                        {/* Latest Period - Sabit Footer */}
                                        {supplier.latestPeriod !== 'N/A' && (
                                            <div style={{ 
                                                marginTop: '12px', 
                                                fontSize: '12px', 
                                                color: '#8c8c8c',
                                                borderTop: '1px solid #f0f0f0',
                                                paddingTop: '8px',
                                                minHeight: '32px' // ✅ Sabit yükseklik
                                            }}>
                                                <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                                {t('latest_period')}: {supplier.latestPeriod}
                                            </div>
                                        )}
                                    </Card>
                                </Badge.Ribbon>
                            </Col>
                        );
                    })}
                </Row>
            )}

            {/* Add Audit Date Modal */}
            <Modal
                title={
                    <span style={{ fontSize: '18px', fontWeight: '600' }}>
                        <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                        {t('add_audit_dates')}
                    </span>
                }
                visible={isModalVisible}
                onOk={handlePostSelectedSuppliers}
                onCancel={() => {
                    setIsModalVisible(false);
                    setSelectedMonth(null);
                }}
                okText={t('create')}
                cancelText={t('cancel')}
                width={500}
            >
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div>
                        <div style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                            {t('selected_suppliers')}
                        </div>
                        <Tag color="blue" style={{ fontSize: '16px', padding: '8px 16px' }}>
                            {selectedSuppliers.length} {t('suppliers')}
                        </Tag>
                    </div>

                    <div>
                        <div style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                            {t('select_starting_month')}
                        </div>
                        <Select
                            placeholder={t('select_a_month')}
                            style={{ width: '100%' }}
                            onChange={setSelectedMonth}
                            value={selectedMonth}
                            size="large"
                        >
                            {Array.from({ length: 12 }, (_, index) => (
                                <Option key={index + 1} value={index + 1}>
                                    {moment().month(index).format('MMMM')} {selectedYear}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div style={{ 
                        background: '#f0f2f5', 
                        padding: '12px', 
                        borderRadius: '8px',
                        fontSize: '13px',
                        color: '#595959'
                    }}>
                        <InfoCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                        {t('audit_dates_will_be_created_based_on_frequency')}
                    </div>
                </Space>
            </Modal>

            {/* Audit History Modal */}
            <Modal
                title={
                    <span style={{ fontSize: '18px', fontWeight: '600' }}>
                        <HistoryOutlined style={{ marginRight: '8px', color: '#faad14' }} />
                        {t('audit_history')} - {selectedSupplierForHistory?.supplierName}
                    </span>
                }
                visible={isHistoryModalVisible}
                onCancel={() => {
                    setIsHistoryModalVisible(false);
                    setSelectedSupplierForHistory(null);
                    setAuditHistory([]);
                }}
                footer={[
                    <Button key="close" onClick={() => setIsHistoryModalVisible(false)}>
                        {t('close')}
                    </Button>
                ]}
                width={700}
            >
                {auditHistory.length === 0 ? (
                    <Empty description={t('no_audit_history')} />
                ) : (
                    <Table
                        dataSource={auditHistory}
                        columns={historyColumns}
                        rowKey="supplierAuditId"
                        pagination={{ pageSize: 10 }}
                        size="small"
                    />
                )}
            </Modal>
        </div>
    );
};

export default SupplierControl;
